import {
	IContractSummaryGridDetail,
	ISupplierContractDocument,
} from "models/ContractManagement/ContractSummaryGridDetail";
import { TableFilterModel } from "models/TableFilterModel";
import { BaseService } from "./base.service";
import {
	NewContractDetailModel,
	IContractAttachment,
	IAgreementType,
	INewContractSystemTemplate,
	AddUpdateNewContractSystemTemplateSection,
	NewContractSystemTemplateModel,
	INewContractTileDetail,
	NewContractSupplierDetail,
	IContractPreview,
	IContractExpiryTileDetail,
	getContractDashboardTileDetail,
	ContractTerminationPOGridListResult,
} from "models/ContractManagement/NewContractDetailModel";
import {
	AddUpdateContractLineModel,
	ContractCompanyWideLineModel,
	ContractLineDetailMilestone,
	ContractLineModel,
	ContractLineModelByCompanyWide,
	IContractAmount,
	IContractItemExistModel,
	IContractLineAttachment,
	IContractLineGridDetail,
} from "models/ContractManagement/ContractLineDetailModel";
import ReadyStatusContractDetail, {
	ICompletedContractStepHistory,
	IPreviousContractStepHistory,
} from "models/ContractManagement/ReadyStatusContractDetail";
import { IContractStepHistory } from "models/ContractManagement/ContractStepHistoryEmitter";
import { IDelegateToUser } from "models/Requisition/DelegateToUserSelection";
import {
	ContractSupplierBidDetailModel,
	IPORequestTabGridFilterDetail,
	IContractSupplierBidAttachment,
	IPORequestsTabDetail,
	IContractPOsTabGridFilterDetail,
	IContractPOTabDetail,
	IPerformanceEvaluationGridDetail,
	PerformanceEvaluationDetailModel,
	IContractPaymentsTabDetail,
	IContractPaymentsTabGridFilterDetail,
	IUnpaidExpenditureTabDetail,
	IUnpaidExpenditureTabGridFilterDetail,
	IVariationsTabGridDetail,
	IVariationTabGridFilterDetail,
} from "models/ContractManagement/ContractTransactionTabsModel";
import { ContractDashboardFilterModel } from "models/Dashboard/DashboardFilterModel";
import {
	IContractAmountByDivisionDetail,
	IContractAmountByProcurementMethodDetail,
	IContractSpendByBbbeeeLevelDetail,
	IContractSupplierBbbeeeSpendSummaryDetail,
	ISupplierOwnershipDetail,
} from "models/Dashboard/ContractManagementDashboardModel";
import { ITableNumberColumnFilter } from "models/TableNumberColumnFilter";
import { IContractNumber } from "models/ContractNumberModel";
import { ContractTerminationModel } from "components/Settings/SystemSetup/ModuleSettingsTabContent/ContractModuleSetting/ExpirySettings/EmailContractTermination/ContractTerminationMaster";
import { IUser } from "models/InternalUser/UserModel";
import { IEvaluationScheduleModel } from "models/ContractManagement/ContractPerformanceEvaluationModel";
import { IContractTakeOnCheckStatusModel } from "models/ContractManagement/ContractTakeOnDetailModel";

class ContractServiceInternal extends BaseService {
	getContractGridList = async (
		model: TableFilterModel
	): Promise<IContractSummaryGridDetail[]> =>
		this.callApi<IContractSummaryGridDetail[]>(
			`Contract/GetContractGridList`,
			"POST",
			undefined,
			model
		);

	submitContractTakeOnImport = async (
		model: FormData
	): Promise<string[] | null> =>
		this.callApi<string[] | null>(
			`ContractTakeOn/ImportTakeOnFiles`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	validateContractTakeOnFile = async (
		model: FormData
	): Promise<{
		contractTakeOnFileId: number;
		contractTakeOnFileDetailId: number;
	}> =>
		this.callApi<{
			contractTakeOnFileId: number;
			contractTakeOnFileDetailId: number;
		}>(
			`ContractTakeOn/ValidateTakeOnFiles`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	deleteContractTakeOnFile = async (
		contractTakeOnFileId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ContractTakeOn/DeleteContractTakeOnFile/${contractTakeOnFileId}`,
			"DELETE",
			undefined,
			undefined,
			undefined,
			true
		);

	deleteContractTakeOnFileDetails = async (
		contractTakeOnFileDetailId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ContractTakeOn/DeleteContractTakeOnFileDetails/${contractTakeOnFileDetailId}`,
			"DELETE",
			undefined,
			undefined,
			undefined,
			true
		);

	getTakeOnValidationStatus = async (
		contractTakeOnFileDetailId: number
	): Promise<IContractTakeOnCheckStatusModel> =>
		this.callApi<IContractTakeOnCheckStatusModel>(
			`ContractTakeOn/GetTakeOnValidationStatus/${contractTakeOnFileDetailId}`,
			"GET",
			undefined
		);

	getTakeOnImportStatus = async (
		contractTakeOnFileId: number
	): Promise<IContractTakeOnCheckStatusModel> =>
		this.callApi<IContractTakeOnCheckStatusModel>(
			`ContractTakeOn/GetTakeOnImportStatus/${contractTakeOnFileId}`,
			"GET",
			undefined
		);

	getContractCount = async (): Promise<number> =>
		this.callApi<number>(`Contract/GetContractCount`, "GET");

	/* Contract Entry Detail - START */
	getContractDetail = async (
		contractId: number,
		contractStepId: number
	): Promise<NewContractDetailModel> =>
		this.callApi<NewContractDetailModel>(
			`Contract/GetContractDetailById/${contractId}`,
			"GET",
			contractStepId > 0
				? new URLSearchParams({
						contractStepId: contractStepId.toString(),
				  })
				: undefined
		);

	saveContractDetail = async (
		model: NewContractDetailModel
	): Promise<{
		contractId: number;
		statusId: number;
		requestNumber: string;
		panelNumber: string;
	}> =>
		this.callApi<{
			contractId: number;
			statusId: number;
			requestNumber: string;
			panelNumber: string;
		}>(`Contract/Save`, "POST", undefined, model);

	updateContractDetail = async (
		model: NewContractDetailModel
	): Promise<number> =>
		this.callApi<number>(
			`Contract/Update/${model.contractId}`,
			"POST",
			undefined,
			model
		);

	deleteContractDetail = async (
		contractId: number,
		contractStepId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Contract/Delete/${contractId}`,
			"DELETE",
			contractStepId > 0
				? new URLSearchParams({
						contractStepId: contractStepId.toString(),
				  })
				: undefined,
			undefined,
			undefined,
			true
		);

	getAgreementType = async (): Promise<IAgreementType[]> =>
		this.callApi<IAgreementType[]>(`AgreementType/GetAll`, "GET");
	/* Contract Entry Detail - END */

	/* Contract Entry Attachment - START */
	getContractAttachment = async (
		contractId: number,
		contractStepId: number
	): Promise<IContractAttachment[]> =>
		this.callApi<IContractAttachment[]>(
			`ContractAttachment/GetContractAttachment/${contractId}`,
			"GET",
			contractStepId > 0
				? new URLSearchParams({
						contractStepId: contractStepId.toString(),
				  })
				: undefined
		);

	saveContractAttachment = async (model: FormData): Promise<number> =>
		this.callApi<number>(
			`ContractAttachment/Save`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	updateContractAttachment = async (
		contractAttachmentId: number,
		model: FormData
	): Promise<number> =>
		this.callApi<number>(
			`ContractAttachment/Update/${contractAttachmentId}`,
			"PUT",
			undefined,
			model,
			false,
			true
		);

	deleteContractAttachment = async (
		contractAttachmentId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ContractAttachment/Delete/${contractAttachmentId}`,
			"DELETE",
			undefined,
			undefined,
			true
		);
	/* Contract Entry Attachment - END */

	/* Contract Entry Line Item - START */
	getContractLineList = async (
		contractId: number,
		contractStepId: number
	): Promise<IContractLineGridDetail> =>
		this.callApi<IContractLineGridDetail>(
			`ContractLine/GetContractLineList/${contractId}`,
			"GET",
			contractStepId && contractStepId > 0
				? new URLSearchParams({
						contractStepId: contractStepId.toString(),
				  })
				: undefined
		);

	getContractLineDetail = async (
		contractLineId: number,
		contractStepId: number
	): Promise<ContractLineModel> =>
		this.callApi<ContractLineModel>(
			`ContractLine/GetContractLineById/${contractLineId}`,
			"GET",
			contractStepId && contractStepId > 0
				? new URLSearchParams({
						contractStepId: contractStepId.toString(),
				  })
				: undefined
		);

	saveContractLine = async (
		model: AddUpdateContractLineModel
	): Promise<{ contractLineId: number }> =>
		this.callApi<{ contractLineId: number }>(
			`ContractLine/Save`,
			"POST",
			undefined,
			model
		);

	updateContractLine = async (
		model: AddUpdateContractLineModel
	): Promise<{ contractLineId: number }> =>
		this.callApi<{ contractLineId: number }>(
			`ContractLine/Update`,
			"PUT",
			undefined,
			model
		);

	deleteContractLine = async (
		contractLineId: number,
		companyWideDepartmentId: number | null
	): Promise<IContractAmount> =>
		this.callApi<IContractAmount>(
			`ContractLine/Delete/${contractLineId}`,
			"DELETE",
			companyWideDepartmentId && companyWideDepartmentId > 0
				? new URLSearchParams({
						companyWideDepartmentId: companyWideDepartmentId.toString(),
				  })
				: undefined
		);

	checkContractItemExist = async (
		model: IContractItemExistModel
	): Promise<{
		isItemAlreadyExist: boolean;
	}> =>
		this.callApi<{
			isItemAlreadyExist: boolean;
		}>(`ContractLine/CheckItemExist`, "POST", undefined, model);

	getContractLineDetailByCompanyWide = async (
		contractLineId: number,
		contractLineHistoryId: number,
		contractStepId: number
	): Promise<ContractLineModelByCompanyWide> => {
		let urlParams = {} as any;
		if (contractLineId && contractLineId > 0)
			urlParams["contractLineId"] = contractLineId.toString();
		if (contractLineHistoryId && contractLineHistoryId > 0)
			urlParams["contractLineHistoryId"] = contractLineHistoryId.toString();
		if (contractStepId && contractStepId > 0)
			urlParams["contractStepId"] = contractStepId.toString();

		return this.callApi<ContractLineModelByCompanyWide>(
			`ContractLine/GetCompanyWideLineItemDetail`,
			"GET",
			Object.keys(urlParams).length ? new URLSearchParams(urlParams) : undefined
		);
	};

	saveUpdateCompanyWideContractLineItem = async (
		model: ContractCompanyWideLineModel[]
	): Promise<number> =>
		this.callApi<number>(
			`ContractLine/SaveUpdateCompanyWideLineItem`,
			"POST",
			undefined,
			model
		);
	/* Contract Entry Line Item - END */

	/* Contract Line Attachment - START */
	getContractLineAttachment = async (
		contractLineId: number,
		contractLineDetailId: number,
		contractStepId: number
	): Promise<IContractLineAttachment[]> => {
		let urlParams = {} as any;
		if (contractLineDetailId > 0) {
			urlParams["contractLineDetailId"] = contractLineDetailId.toString();
		}
		if (contractStepId > 0) {
			urlParams["contractStepId"] = contractStepId.toString();
		}
		return this.callApi<IContractLineAttachment[]>(
			`ContractLineAttachment/GetContractLineAttachmentList/${contractLineId}`,
			"GET",
			new URLSearchParams(urlParams)
		);
	};

	saveContractLineAttachment = async (model: FormData): Promise<number> =>
		this.callApi<number>(
			`ContractLineAttachment/Save`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	updateContractLineAttachment = async (
		contractLineAttachmentId: number,
		model: FormData
	): Promise<{ contractLineAttachmentId: number }> =>
		this.callApi<{ contractLineAttachmentId: number }>(
			`ContractLineAttachment/Update/${contractLineAttachmentId}`,
			"PUT",
			undefined,
			model,
			false,
			true
		);

	deleteContractLineAttachment = async (
		contractLineAttachmentId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ContractLineAttachment/Delete/${contractLineAttachmentId}`,
			"DELETE",
			undefined,
			undefined,
			true
		);
	/* Contract Line Attachment - END */

	/* Contract Line Milestone - START */
	getContractLineDetailMilestone = async (
		contractLineDetailId: number
	): Promise<ContractLineDetailMilestone[]> =>
		this.callApi<ContractLineDetailMilestone[]>(
			`ContractLineDetailMilestone/GetMilestonesByContractLineDetailId/${contractLineDetailId}`,
			"GET"
		);

	saveContractLineDetailMilestone = async (
		model: ContractLineDetailMilestone[]
	): Promise<{ contractLineDetailId: number; totalMilestoneAmount: number }> =>
		this.callApi<{
			contractLineDetailId: number;
			totalMilestoneAmount: number;
		}>(`ContractLineDetailMilestone/SaveUpdateAll`, "POST", undefined, model);

	deleteContractLineDetailMilestone = async (
		contractLineDetailMilestoneId: number
	): Promise<number> =>
		this.callApi<number>(
			`ContractLineDetailMilestone/Delete/${contractLineDetailMilestoneId}`,
			"DELETE"
		);
	/* Contract Line Milestone - END */

	/* Submit Contract Detail */
	submitNewContract = async (
		contractId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Contract/SubmitContract/${contractId}`,
			"PUT",
			undefined,
			{
				comment: comment,
			}
		);
	/* Submit Contract Detail - END */

	/* Contract Detail Steps and Email - START */
	getReadyStatusContractDetails = async (
		contractId: string,
		contractStepId: string
	): Promise<ReadyStatusContractDetail> =>
		this.callApi<ReadyStatusContractDetail>(
			`Contract/GetReadyStatusContractDetails`,
			"POST",
			undefined,
			{
				Key: contractId.toString(),
				Key_1: contractStepId.toString(),
			}
		);

	getContractStepList = async (
		contractId: number
	): Promise<IContractStepHistory[]> =>
		this.callApi<IContractStepHistory[]>(
			`ContractStep/GetContractStepList/${contractId}`,
			"GET"
		);

	updateContractStepCompleteAndApprove = async (
		contractId: number,
		contractStepId: number,
		comment: string
	): Promise<{ isThisLastStep: boolean }> =>
		this.callApi<{ isThisLastStep: boolean }>(
			`Contract/UpdateContractStepCompleteAndApprove/${contractId}/${contractStepId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	rejectContract = async (
		contractId: number,
		contractStepId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Contract/RejectContract/${contractId}/${contractStepId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	getStepsBeforeCurrentStep = async (
		contractId: number,
		contractStepId: number
	): Promise<IPreviousContractStepHistory[]> =>
		this.callApi<IPreviousContractStepHistory[]>(
			`ContractStep/GetStepsBeforeCurrentStep/${contractId}/${contractStepId}`,
			"GET"
		);

	updateContractStepStatusChangeRequest = async (
		contractId: number,
		selectedStepContractStepId: number,
		currentStepContractStepId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Contract/UpdateContractStepStatusChangeRequest/${contractId}`,
			"POST",
			undefined,
			{
				comment: comment,
				selectedStepContractStepId: selectedStepContractStepId.toString(),
				currentStepContractStepId: currentStepContractStepId.toString(),
			}
		);

	delegateContract = async (
		contractId: number,
		contractStepId: number,
		userId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Contract/DelegateContract/${contractId}/${contractStepId}`,
			"POST",
			undefined,
			{
				comment: comment,
				userId: userId.toString(),
			}
		);

	getDelegatedUserList = async (
		contractStepId: number
	): Promise<IDelegateToUser[]> =>
		this.callApi<IDelegateToUser[]>(
			`Contract/GetDelegatedUserList/${contractStepId}`,
			"GET"
		);

	getCompletedContractStepDetail = async (
		contractId: number
	): Promise<ICompletedContractStepHistory> =>
		this.callApi<ICompletedContractStepHistory>(
			`ContractStep/GetCompletedContractStepDetail/${contractId}`,
			"GET"
		);

	updateContractStepStatusRecall = async (
		contractId: number,
		completedStepContractStepId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Contract/UpdateContractStepStatusRecall/${contractId}`,
			"POST",
			undefined,
			{
				completedStepContractStepId: completedStepContractStepId.toString(),
				comment: comment,
			}
		);

	checkIsContractStepReady = async (contractStepId: number): Promise<boolean> =>
		this.callApi<boolean>(
			`Contract/CheckIsContractStepReady/${contractStepId}`,
			"GET"
		);

	reSubmitNewContract = async (
		contractId: number,
		currentStepContractStepId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`Contract/ReSubmitContract/${contractId}/${currentStepContractStepId}`,
			"POST",
			undefined,
			{
				comment: comment,
			}
		);

	/* Contract Detail Steps and Email - END */

	/* Contract System Template */
	getSystemTemplates = async (
		purchaseTypeId: number,
		purchaseCategoryId: number
	): Promise<INewContractSystemTemplate> =>
		this.callApi<INewContractSystemTemplate>(
			`Contract/GetSystemTemplates/${purchaseTypeId}/${purchaseCategoryId}`,
			"GET"
		);

	getContractSystemTemplates = async (
		contractId: number
	): Promise<INewContractSystemTemplate> =>
		this.callApi<INewContractSystemTemplate>(
			`Contract/GetContractSystemTemplates/${contractId}`,
			"GET"
		);

	previewContractSystemTemplate = async (contractId: number): Promise<string> =>
		this.callApi<string>(
			`ContractSystemTemplateSection/Preview/${contractId}`,
			"GET"
		);

	saveContractSystemTemplateSection = async (
		model: AddUpdateNewContractSystemTemplateSection
	): Promise<NewContractSystemTemplateModel[]> =>
		this.callApi<NewContractSystemTemplateModel[]>(
			`ContractSystemTemplateSection/Save`,
			"POST",
			undefined,
			model
		);

	updateContractSystemTemplateSection = async (
		model: AddUpdateNewContractSystemTemplateSection
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ContractSystemTemplateSection/Update/${model.contractId}`,
			"PUT",
			undefined,
			model
		);
	/* Contract System Template - END */

	/* Contract Supplier (Entity ownership) - START */
	getNewContractSupplierDetail = async (
		supplierId: number
	): Promise<NewContractSupplierDetail> =>
		this.callApi<NewContractSupplierDetail>(
			`Supplier/GetSupplierDetail/${supplierId}`,
			"GET"
		);

	saveUpdateNewContractSupplier = async (
		model: FormData
	): Promise<{ supplierId: number; companyName: string }> =>
		this.callApi<{ supplierId: number; companyName: string }>(
			`Supplier/SaveUpdate`,
			"POST",
			undefined,
			model,
			false,
			true
		);
	/* Contract Supplier (Entity ownership) - END */

	/* Supplier Bid Tab - START */
	getContractSupplierBidById = async (
		contractId: number,
		supplierId: number,
		panelNumber: string,
		contractStepId: number
	): Promise<ContractSupplierBidDetailModel> => {
		let urlParams = {} as any;
		if (supplierId > 0) urlParams["supplierId"] = supplierId.toString();
		if (contractStepId && contractStepId > 0)
			urlParams["contractStepId"] = contractStepId.toString();
		if (panelNumber) urlParams["panelNumber"] = panelNumber;
		return this.callApi<ContractSupplierBidDetailModel>(
			`ContractSupplierBid/GetDetailById/${contractId}`,
			"GET",
			new URLSearchParams(urlParams)
		);
	};

	saveContractSupplierBid = async (
		model: ContractSupplierBidDetailModel
	): Promise<number> =>
		this.callApi<number>(`ContractSupplierBid/Save`, "POST", undefined, model);

	updateContractSupplierBid = async (
		model: ContractSupplierBidDetailModel
	): Promise<number> =>
		this.callApi<number>(
			`ContractSupplierBid/Update/${model.contractSupplierBidId}`,
			"POST",
			undefined,
			model
		);

	getContractSupplierBidAttachment = async (
		contractSupplierBidId: number,
		supplierId: number,
		contractStepId: number
	): Promise<IContractSupplierBidAttachment[]> => {
		let urlParams = {} as any;
		if (contractStepId && contractStepId > 0)
			urlParams["contractStepId"] = contractStepId.toString();
		return this.callApi<IContractSupplierBidAttachment[]>(
			`ContractSupplierBidAttachment/GetContractSupplierBidAttachment/${contractSupplierBidId}/${supplierId}`,
			"GET",
			new URLSearchParams(urlParams)
		);
	};

	saveContractSupplierBidAttachment = async (
		model: FormData
	): Promise<number> =>
		this.callApi<number>(
			`ContractSupplierBidAttachment/Save`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	updateContractSupplierBidAttachment = async (
		contractSupplierBidAttachmentId: number,
		model: FormData
	): Promise<{ contractSupplierBidAttachmentId: number }> =>
		this.callApi<{ contractSupplierBidAttachmentId: number }>(
			`ContractSupplierBidAttachment/Update/${contractSupplierBidAttachmentId}`,
			"PUT",
			undefined,
			model,
			false,
			true
		);

	deleteContractSupplierBidAttachment = async (
		contractSupplierBidAttachmentId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ContractSupplierBidAttachment/Delete/${contractSupplierBidAttachmentId}`,
			"DELETE",
			undefined,
			undefined,
			true
		);

	checkIfContractSupplierBidAttachmentExists = async (
		contractId: number,
		supplierId: number,
		panelNumber: string
	): Promise<boolean> =>
		this.callApi<boolean>(
			`ContractSupplierBidAttachment/ContractSupplierBidAttachmentExists/${contractId}/${supplierId}`,
			"GET",
			panelNumber
				? new URLSearchParams({ panelNumber: panelNumber })
				: undefined
		);

	/* Supplier Bid Tab - END */

	/* Performance Evaluation  Tab - START */
	getPerformanceEvaluationList = async (): Promise<
		IPerformanceEvaluationGridDetail[]
	> =>
		this.callApi<IPerformanceEvaluationGridDetail[]>(
			`EvaluationCategory/GetEvaluationCategoryAndCriteriaList`,
			"GET"
		);

	getContractPerformanceEvaluationListById = async (
		contractId: number
	): Promise<PerformanceEvaluationDetailModel> =>
		this.callApi<PerformanceEvaluationDetailModel>(
			`Contract/GetContractPerformanceEvaluation/${contractId}`,
			"POST"
		);

	saveContractPerformanceEvaluation = async (
		model: PerformanceEvaluationDetailModel
	): Promise<number> =>
		this.callApi<number>(
			`Contract/SaveContractPerformanceEvaluation`,
			"POST",
			undefined,
			model
		);

	/* Performance Evaluation Tab - END */

	getContractTileDetail = async (
		model: TableFilterModel
	): Promise<INewContractTileDetail> =>
		this.callApi<INewContractTileDetail>(
			`Contract/GetContractTilesTotal`,
			"POST",
			undefined,
			model
		);

	copyContract = async (
		contractId: number
	): Promise<{ contractId: number; requestNumber: string }> =>
		this.callApi<{ contractId: number; requestNumber: string }>(
			`Contract/Copy/${contractId}`,
			"POST"
		);

	/* Upload Signed Contract */
	getContractSignedDocument = async (
		contractId: number
	): Promise<ISupplierContractDocument[]> =>
		this.callApi<ISupplierContractDocument[]>(
			`ContractAttachment/GetContractSignedDocumentList/${contractId}`,
			"GET"
		);

	uploadContractSignedDocument = async (
		model: FormData,
		isForceUpdate: boolean
	): Promise<any> =>
		this.callApi<any>(
			`ContractAttachment/UploadContractSignedDocument?isForceUpdate=${isForceUpdate}`,
			"POST",
			undefined,
			model,
			false,
			true
		);
	/* Upload Signed Contract - END */

	/* PO Request Grid  */
	getPORequestList = async (
		contractId: number,
		model: TableFilterModel
	): Promise<IPORequestsTabDetail | null> =>
		this.callApi<IPORequestsTabDetail | null>(
			`Contract/GetContractPOList/${contractId}`,
			"POST",
			undefined,
			model
		);

	/* Get Contract Requisition Po Filter By Contract Id */
	getContractRequisitionPoFilter = async (
		contractId: number
	): Promise<IPORequestTabGridFilterDetail[]> =>
		this.callApi<IPORequestTabGridFilterDetail[]>(
			`Contract/GetContractRequisitionPoFilter/${contractId}`,
			"POST"
		);

	/* PO Request Grid - END */

	/* Variation Request Grid  */
	getVariationList = async (
		contractId: number,
		model: TableFilterModel
	): Promise<IVariationsTabGridDetail[] | null> =>
		this.callApi<IVariationsTabGridDetail[] | null>(
			`Contract/GetContractVariationList/${contractId}`,
			"POST",
			undefined,
			model
		);

	/* Get Contract Requisition Po Filter By Contract Id */
	getContractVariationFilter = async (
		contractId: number
	): Promise<IVariationTabGridFilterDetail[]> =>
		this.callApi<IVariationTabGridFilterDetail[]>(
			`Contract/GetContractRequisitionVariationFilter/${contractId}`,
			"POST"
		);

	/* Variation Grid - END */

	/* Contract PO Grid */
	getContractPOList = async (
		contractId: number,
		model: TableFilterModel
	): Promise<IContractPOTabDetail | null> =>
		this.callApi<IContractPOTabDetail | null>(
			`Contract/GetContractPOGridList/${contractId}`,
			"POST",
			undefined,
			model
		);
	/* Get Contract Po Filter By Contract Id */
	getContractPoFilter = async (
		contractId: number
	): Promise<IContractPOsTabGridFilterDetail[]> =>
		this.callApi<IContractPOsTabGridFilterDetail[]>(
			`Contract/GetContractPoFilter/${contractId}`,
			"POST"
		);
	/* Contract PO Grid - END */

	/* Contract Unpaid Expenditure Grid */
	getUnpaidExpenditureList = async (
		contractId: number,
		model: TableFilterModel
	): Promise<IUnpaidExpenditureTabDetail | null> =>
		this.callApi<IUnpaidExpenditureTabDetail | null>(
			`Contract/GetContractUnpaidExpenditureGridList/${contractId}`,
			"POST",
			undefined,
			model
		);
	/* Get Contract Unpaid Expenditure Filter By Contrarct Id */
	getUnpaidExpenditureFilter = async (
		contractId: number
	): Promise<IUnpaidExpenditureTabGridFilterDetail[]> =>
		this.callApi<IUnpaidExpenditureTabGridFilterDetail[]>(
			`Contract/GetContractUnpaidExpenditureFilter/${contractId}`,
			"POST"
		);
	/* Contract Unpaid Expenditure Grid - END */

	/* Contract Payments Grid */
	getContractPaymentsList = async (
		contractId: number,
		model: TableFilterModel
	): Promise<IContractPaymentsTabDetail | null> =>
		this.callApi<IContractPaymentsTabDetail | null>(
			`Contract/GetContractPaymentGridList/${contractId}`,
			"POST",
			undefined,
			model
		);
	/* Get Contract Payments Filter By Contrarct Id */
	getContractPaymentsFilter = async (
		contractId: number
	): Promise<IContractPaymentsTabGridFilterDetail[]> =>
		this.callApi<IContractPaymentsTabGridFilterDetail[]>(
			`Contract/GetContractPaymentFilter/${contractId}`,
			"POST"
		);
	/* Contract Payments Grid - END */

	/* Contract management dashboard - start*/
	getContractPreview = async (contractId: number): Promise<IContractPreview> =>
		this.callApi<IContractPreview>(
			`Contract/GetContractPreview/${contractId}`,
			"POST"
		);

	getContractManagementDashboardFilter =
		async (): Promise<ContractDashboardFilterModel | null> =>
			this.callApi<ContractDashboardFilterModel | null>(
				`Contract/GetContractDashboardDropDownList`,
				"GET"
			);

	getContractAmountByProcurementMethod = async (
		model: TableFilterModel
	): Promise<IContractAmountByProcurementMethodDetail> =>
		this.callApi<IContractAmountByProcurementMethodDetail>(
			`Contract/GetContractAmountByProcurementMethod`,
			"POST",
			undefined,
			model
		);

	getContractSpendByBbbeeLevel = async (
		model: TableFilterModel
	): Promise<IContractSpendByBbbeeeLevelDetail> =>
		this.callApi<IContractSpendByBbbeeeLevelDetail>(
			`Contract/GetContractSpendByBbbeeLevel`,
			"POST",
			undefined,
			model
		);

	getContractAmountByDivision = async (
		model: TableFilterModel
	): Promise<IContractAmountByDivisionDetail> =>
		this.callApi<IContractAmountByDivisionDetail>(
			`Contract/GetContractAmountByDivision`,
			"POST",
			undefined,
			model
		);

	getContractExpiryTileDetail = async (
		model: TableFilterModel
	): Promise<IContractExpiryTileDetail> =>
		this.callApi<IContractExpiryTileDetail>(
			`Contract/GetContractExpiryDetailsTiles`,
			"POST",
			undefined,
			model
		);

	getContractSupplierBbbeeSpendSummary = async (
		model: TableFilterModel
	): Promise<IContractSupplierBbbeeeSpendSummaryDetail[]> =>
		this.callApi<IContractSupplierBbbeeeSpendSummaryDetail[]>(
			`Contract/GetContractSupplierBbbeeSpendSummary`,
			"POST",
			undefined,
			model
		);

	getSupplierOwnershipSummaryData = async (
		model: TableFilterModel
	): Promise<ISupplierOwnershipDetail> =>
		this.callApi<ISupplierOwnershipDetail>(
			`Contract/GetContractSupplierSummaryWithOwnershipData`,
			"POST",
			undefined,
			model
		);

	getContractNumberList = async (
		model: ITableNumberColumnFilter
	): Promise<IContractNumber[]> =>
		this.callApi<IContractNumber[]>(
			`Contract/GetContractNumberList`,
			"POST",
			undefined,
			model
		);

	getContractDashboardTileDetail = async (
		model: TableFilterModel
	): Promise<getContractDashboardTileDetail> =>
		this.callApi<getContractDashboardTileDetail>(
			`Contract/GetContractDashboardTileDetail`,
			"POST",
			undefined,
			model
		);
	/* Contract management dashboard - end*/

	/* Contract Expiry - Start*/
	terminationScheduledContract = async (
		model: ContractTerminationModel
	): Promise<boolean> => {
		return this.callApi<boolean>(
			`Contract/TerminationScheduledContract`,
			"POST",
			undefined,
			model
		);
	};
	/* Contract Expiry - End*/

	scheduleArchiveContract = async (contractId: number): Promise<any> => {
		return this.callApi<any>(
			`/Contract/ScheduleArchiveContract/${contractId}`,
			"POST"
		);
	};

	getPerformanceEvaluationUsers = async (
		contractId: number
	): Promise<IUser[]> =>
		this.callApi<IUser[]>(
			`Contract/GetPerformanceEvaluationUsers/${contractId}`,
			"GET"
		);

	getEvaluationScheduleList = async (
		notificationFrequencyId: number
	): Promise<IEvaluationScheduleModel[]> =>
		this.callApi<IEvaluationScheduleModel[]>(
			`EvaluationSchedule/GetEvaluationScheduleList/${notificationFrequencyId}`,
			"GET",
			undefined,
			undefined,
			undefined,
			undefined,
			false
		);

	getContractMeeting = async (contractId: number): Promise<number> =>
		this.callApi<number>(`Contract/GetContractMeeting/${contractId}`, "GET");
	holdContract = async (
		data: { contractId: number; comment: string; files: File[] },
		isHoldRemoval?: boolean
	): Promise<any> => {
		const formData = new FormData();
		formData.append("ContractId", data.contractId.toString());
		formData.append("Comment", data.comment);

		data.files.forEach((file) => {
			formData.append("File", file);
		});

		const urlParams = isHoldRemoval
			? new URLSearchParams({ isHoldRemoval: "true" })
			: new URLSearchParams({ isHoldRemoval: "false" });

		return this.callApi<any>(
			"Contract/HoldContract",
			"POST",
			urlParams,
			formData,
			false, // skipReadingResponseBody
			true // isFileUpload
		);
	};

	getContractTerminationPOGridList = async (
		contractId: number
	): Promise<ContractTerminationPOGridListResult> =>
		this.callApi<ContractTerminationPOGridListResult>(
			`Contract/GetContractTerminationPOGridList/${contractId}`,
			"GET"
		);

	terminateContract = async (
		data: { contractId: number; comment: string; files: File[] },
		isTerminationRemoval?: boolean,
		isDeleteHoldWorkflow?: boolean
	): Promise<any> => {
		const formData = new FormData();
		formData.append("ContractId", data.contractId.toString());
		formData.append("Comment", data.comment);

		data.files.forEach((file) => {
			formData.append("File", file);
		});

		const urlParams = new URLSearchParams({
			isTerminationRemoval: String(!!isTerminationRemoval),
			isDeleteHoldWorkflow: String(!!isDeleteHoldWorkflow),
		});

		return this.callApi<any>(
			"Contract/TerminateContract",
			"POST",
			urlParams,
			formData,
			false, // skipReadingResponseBody
			true // isFileUpload
		);
	};

	getContractHoldAttachmentsByStepId = async (
		stepId: number
	): Promise<IContractAttachment[]> =>
		this.callApi<IContractAttachment[]>(
			`Contract/GetContractHoldAttachmentsByStepId/${stepId}`,
			"GET"
		);

	saveHoldContractAttachment = async (
		stepId: number,
		model: FormData
	): Promise<number> =>
		this.callApi<number>(
			`Contract/SaveHoldContractAttachment/${stepId}`,
			"POST",
			undefined,
			model,
			false, // skipReadingResponseBody
			true // isFileUpload
		);

	getContractTerminationAttachmentsByStepId = async (
		stepId: number
	): Promise<IContractAttachment[]> =>
		this.callApi<IContractAttachment[]>(
			`Contract/GetContractTerminationAttachmentsByStepId/${stepId}`,
			"GET"
		);

	saveTerminationContractAttachment = async (
		stepId: number,
		model: FormData
	): Promise<number> =>
		this.callApi<number>(
			`Contract/SaveTerminationContractAttachment/${stepId}`,
			"POST",
			undefined,
			model,
			false, // skipReadingResponseBody
			true // isFileUpload
		);

	updateContractHoldStepStatusRecall = async (
		contractId: number,
		model: {
			completedStepContractStepId: number;
			comment: string;
			files?: File[];
		},
		isDelete?: boolean
	): Promise<number> => {
		const formData = new FormData();
		formData.append(
			"CompletedStepContractStepId",
			model.completedStepContractStepId.toString()
		);
		formData.append("Comment", model.comment);
		model.files?.forEach((file) => formData.append("File", file));

		return this.callApi<number>(
			`Contract/UpdateContractHoldStepStatusRecall/${contractId}`,
			"POST",
			isDelete ? new URLSearchParams({ isDelete: "true" }) : undefined,
			formData,
			false, // skipReadingResponseBody
			true // isFileUpload
		);
	};
	updateContractTerminationStepStatusRecall = async (
		contractId: number,
		model: {
			completedStepContractStepId: number;
			comment: string;
			files?: File[];
		},
		isDelete?: boolean
	): Promise<number> => {
		const formData = new FormData();
		formData.append(
			"CompletedStepContractStepId",
			model.completedStepContractStepId.toString()
		);
		formData.append("Comment", model.comment);
		model.files?.forEach((file) => formData.append("File", file));

		return this.callApi<number>(
			`Contract/UpdateContractTerminationStepStatusRecall/${contractId}`,
			"POST",
			isDelete ? new URLSearchParams({ isDelete: "true" }) : undefined,
			formData,
			false, // skipReadingResponseBody
			true // isFileUpload
		);
	};
	updateHoldContractAttachment = async (
		contractAttachmentId: number,
		model: FormData
	): Promise<number> =>
		this.callApi<number>(
			`Contract/UpdateHoldContractAttachment/${contractAttachmentId}`,
			"PUT",
			undefined,
			model,
			false,
			true
		);
	updateTerminatContractAttachment = async (
		contractAttachmentId: number,
		model: FormData
	): Promise<number> =>
		this.callApi<number>(
			`Contract/UpdateTerminationContractAttachment/${contractAttachmentId}`,
			"PUT",
			undefined,
			model,
			false,
			true
		);
}

export const ContractService = new ContractServiceInternal();
